import { memo, useEffect, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Popover } from '@headlessui/react';
import { ClipPublishedStatus } from '@goldcast/player-config/src/types/asset-types';
import DuplicateRecordingRow from './DuplicateRecordingRow';
import OriginalRecordingRow from './OriginalRecordingRow';
import DuplicateRecordingModal from './DuplicateRecordingModal';
import Icon from '@/components/atoms/Icon';
import { core } from '@/stores/core';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRecordingDuplicatesStore, updateRecordingDuplicate } from '@/stores/duplicateRecordingStore';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import useCreateClip from '@/Pages/Clip/ClipPlayer/hooks/useCreateClip';
import useAnalytics from '@/hooks/useAnalytics';
import { showErrorToast } from '@/libs/toast/toast';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { PUSHER_STATES } from '@/context/PusherContext/PusherContextConstants';

interface RecordingItem {
  id: string;
  title: string;
  label?: string;
  isOriginal?: boolean;
}

function RecordingDuplicateDropdown() {
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const { clipData } = useClipsContext();
  const recordings = useRecordingDuplicatesStore(state => state.recordings);
  const isLoading = useRecordingDuplicatesStore(state => state.isLoading);
  const hasLoadError = useRecordingDuplicatesStore(state => state.hasLoadError);
  const loadRecordings = useRecordingDuplicatesStore(state => state.loadRecordings);
  const removeRecording = useRecordingDuplicatesStore(state => state.removeRecording);
  const [, setSearchParams] = useSearchParams();

  const { onCreateDuplicate } = useCreateClip({ clip: clipData });
  const { trackRecordingDuplicate, trackRecordingDuplicateDropdownClick, trackRecordingDuplicateCreateClick } =
    useAnalytics();

  const [isCreating, setIsCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const recordingTitle = coreStore.content?.title || 'Original';
  const activeClipId = clipData.id;
  const originalClipId = coreStore.content?.full_length_asset_id ?? activeClipId;
  const broadcastId = coreStore.content?.id ?? '';

  const entry = recordings[broadcastId];
  const originalRecording = entry?.original;
  const duplicates = entry?.duplicates ?? [];
  const activeDuplicate = duplicates.find(d => d.id === activeClipId);
  const activeRecordingTitle = activeClipId === originalClipId ? 'Original' : (activeDuplicate?.title ?? 'Original');

  const existingTitles = new Set(duplicates.map(d => d.title));
  const baseCopyName = `Copy of ${activeRecordingTitle}`;
  let defaultDuplicateName = baseCopyName;
  if (existingTitles.has(baseCopyName)) {
    let counter = 2;
    while (existingTitles.has(`Copy ${counter} of ${activeRecordingTitle}`)) {
      counter++;
    }
    defaultDuplicateName = `Copy ${counter} of ${activeRecordingTitle}`;
  }

  const handleConfirmDuplicate = async (name: string) => {
    if (isCreating) {
      return;
    }
    setIsCreating(true);
    trackRecordingDuplicate({ action: 'Create', state: 'Started', sourceRecordingId: originalClipId });
    try {
      const newRecording = await onCreateDuplicate(name);
      trackRecordingDuplicate({ action: 'Create', state: 'Success', sourceRecordingId: originalClipId });
      setIsModalOpen(false);
      if (newRecording?.id) {
        setSearchParams(
          prev => {
            prev.set('activeDuplicateId', newRecording.id);
            return prev;
          },
          { replace: true }
        );
      }
    } catch (err) {
      trackRecordingDuplicate({
        action: 'Create',
        state: 'Failed',
        sourceRecordingId: originalClipId,
        failureReason: err instanceof Error ? err.message : String(err)
      });
      showErrorToast('Failed to create duplicate. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    if (!featureFlags[FeatureFlagKeys.Use_CL_Clip_Duplication] || !broadcastId) {
      return;
    }
    loadRecordings(broadcastId);
  }, [broadcastId, featureFlags, loadRecordings]);

  useEffect(() => {
    const listener = EventBus.on(CustomEvents.PublishRecordingStatusUpdated, ({ data }) => {
      const { upload_id, broadcast_id, asset_id, state } = data;
      const contentID = upload_id || broadcast_id;
      const { recordings } = useRecordingDuplicatesStore.getState();
      const contentEntry = recordings[contentID];
      // The status update can target either the original recording or one of its duplicates;
      // `updateRecordingDuplicate` routes the write to whichever one matches.
      const existing =
        contentEntry?.original?.id === asset_id
          ? contentEntry.original
          : contentEntry?.duplicates.find(r => r.id === asset_id);
      if (!existing) {
        return;
      }

      const publishStatus =
        state === PUSHER_STATES.PUBLISH_STATUS_UPDATED_DONE
          ? ClipPublishedStatus.PUBLISHED
          : ClipPublishedStatus.FAILED;

      updateRecordingDuplicate(contentID, {
        ...existing,
        asset_metadata: { ...existing.asset_metadata, publish_status: publishStatus }
      });
    });

    return () => EventBus.off(CustomEvents.PublishRecordingStatusUpdated, listener);
  }, []);

  const recordingList: RecordingItem[] = [
    { id: originalClipId, title: recordingTitle, label: 'Original', isOriginal: true },
    ...duplicates
  ];

  const isActive = (id: string) => id === activeClipId;

  const handleDuplicateDeleted = (recordingId: string) => {
    removeRecording(broadcastId, recordingId);
    // If the deleted duplicate is the one currently open, fall back to the original recording.
    if (recordingId === activeClipId) {
      setSearchParams(
        prev => {
          prev.delete('activeDuplicateId');
          return prev;
        },
        { replace: true }
      );
    }
  };

  const handleSelectRecording = (id: string) => {
    const isOriginal = recordingList.find(recording => recording.id === id && recording.isOriginal);
    // Only a switch to a duplicate is a "duplicate opened"; returning to the original isn't.
    if (!isOriginal && id !== activeClipId) {
      trackRecordingDuplicate({ action: 'Open', recordingId: id, sourceRecordingId: originalClipId });
    }
    setSearchParams(
      prev => {
        if (isOriginal) {
          prev.delete('activeDuplicateId');
        } else {
          prev.set('activeDuplicateId', id);
        }
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <Popover className="relative z-50">
      {({ open }) => (
        <>
          <Popover.Button
            className="flex items-center rounded p-1 text-slate-900 hover:bg-slate-200 focus:outline-none"
            aria-label="Duplicate recordings"
            onClick={trackRecordingDuplicateDropdownClick}
          >
            <Icon name={open ? 'IconChevronUp' : 'IconChevronDown'} size={14} stroke={2.5} />
          </Popover.Button>

          <Popover.Panel
            data-testid="duplicate-recordings-dropdown"
            className="fixed z-50 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-1 shadow-lg transition duration-150 ease-out [--anchor-gap:8px]"
          >
            {/* Recordings list. The original recording renders through OriginalRecordingRow (download
                  only); duplicates render through DuplicateRecordingRow (download + delete). */}
            <div className="max-h-[30vh] overflow-auto">
              <OriginalRecordingRow
                clipId={originalClipId}
                title={recordingTitle}
                recording={originalRecording}
                isActive={isActive(originalClipId)}
                onSelect={() => handleSelectRecording(originalClipId)}
              />
              {isLoading ? (
                <div className="p-2 text-sm text-slate-900">Loading...</div>
              ) : hasLoadError ? (
                <div className="p-2 text-sm text-red-600">Failed to load duplicate clips</div>
              ) : (
                duplicates.map(recording => (
                  <DuplicateRecordingRow
                    key={recording.id}
                    recording={recording}
                    isActive={isActive(recording.id)}
                    onSelect={() => handleSelectRecording(recording.id)}
                    onDeleted={handleDuplicateDeleted}
                  />
                ))
              )}
            </div>

            <hr className="my-1 border-slate-100" />

            <button
              disabled={isCreating}
              onClick={() => {
                trackRecordingDuplicateCreateClick();
                setIsModalOpen(true);
              }}
              className="flex w-full items-center gap-2 rounded p-2 text-sm text-slate-900 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              <Icon name="IconPlus" size={14} />
              <span>{isCreating ? 'Creating...' : 'Create duplicate'}</span>
            </button>
          </Popover.Panel>

          {isModalOpen && (
            <DuplicateRecordingModal
              isOpen={isModalOpen}
              defaultName={defaultDuplicateName}
              isCreating={isCreating}
              onClose={() => setIsModalOpen(false)}
              onConfirm={handleConfirmDuplicate}
            />
          )}
        </>
      )}
    </Popover>
  );
}

export default memo(RecordingDuplicateDropdown);
